import axios from "axios";
import { getLocalToken, getHost, getHeaders, getAuthToken } from "./config";
import { refreshToken } from "../services/BasicAPIFunctions";
import {
  clearStoredAuth,
  getStoredRefreshToken,
  persistTokens,
} from "../../lib/auth";
import {
  isExpiringSoon,
  isSessionRejected,
  singleFlight,
} from "../../lib/tokenRefresh";

/*
 * The one axios instance for signed-in calls, and the only place the
 * sign-in is renewed or ended.
 *
 * The blank-sidebar bug lived here. Every 401 renewed the token on its own,
 * so the first page of the morning fired a renewal per request. The backend
 * keeps one token per session, so only the last renewal's token stayed valid
 * and the rest failed again. The refresh route shares the office's rate
 * limit, so one of the burst came back 429. A failed renewal wiped the saved
 * sign-in and sent the tab to the sign-in page, and a successful renewal from
 * the same burst landed after the wipe and wrote the token back. The app then
 * booted with a token and nothing else: no profile, no permissions, no menu.
 *
 * Now:
 *  - a token about to expire is renewed before the request goes out;
 *  - callers share one renewal per tab, and tabs take turns through a Web
 *    Lock, so the browser renews once however many pages are open;
 *  - a tab that finds a newer token saved by another tab uses it instead of
 *    renewing again;
 *  - a renewal the server did not refuse (timeout, 429, 5xx) fails that one
 *    request and keeps the session;
 *  - ending the session happens once and is final: nothing is written to
 *    storage after it.
 */

const axiosInstance = axios.create({
  baseURL: getHost(),
  headers: {},
});

// Shared by every tab of this origin, so renewals never overlap.
const RENEWAL_LOCK = "nysmah-admin-session-renewal";

let sessionEnded = false;

/** Clear the saved sign-in and go to the sign-in page. Once, and final. */
export const endSession = () => {
  if (sessionEnded) return;
  sessionEnded = true;
  clearStoredAuth();
  window.location.assign(`${process.env.PUBLIC_URL}/`);
};

const refused = (message) =>
  Object.assign(new Error(message), { sessionRejected: true });

// Tabs take turns. Browsers without Web Locks (none we support, but the test
// runner is one) just run the renewal.
const withRenewalLock = (task) =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request(RENEWAL_LOCK, task)
    : task();

/**
 * Renew the access token. `staleToken` is the token that expired or was
 * refused. Concurrent callers in this tab share one call, and a token saved
 * meanwhile by another tab is used as it is.
 *
 * Resolves with the token to send. Rejects with an error isSessionRejected()
 * can read: true when the server refused the refresh token, false for a
 * timeout, a rate limit or a dropped connection.
 */
export const renewSession = singleFlight((staleToken) =>
  withRenewalLock(async () => {
    const saved = getLocalToken();
    if (saved && saved !== staleToken && !isExpiringSoon(saved)) {
      return saved; // another tab renewed while we waited for the lock
    }
    const refresh = getStoredRefreshToken();
    if (!refresh) throw refused("No refresh token saved");
    const response = await refreshToken({ refresh_token: refresh });
    const token = response?.data?.response?.access_token;
    if (typeof token !== "string" || token === "") {
      throw refused("Access token missing in refresh response");
    }
    if (sessionEnded) throw refused("Session ended during renewal");
    persistTokens({ token });
    return token;
  }),
);

/** The token a request went out with, read back from its headers. */
const sentToken = (request) => {
  const headers = request?.headers;
  const header =
    typeof headers?.get === "function"
      ? headers.get("authorization")
      : headers?.authorization ?? headers?.Authorization;
  return typeof header === "string" ? header.replace(/^Bearer\s+/i, "") : null;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    let token = getLocalToken();

    // About to expire (the first request of most mornings): renew first, so
    // the page's request does not make a 401 round trip before it starts.
    // `skipRenewal` is a per-request opt-out.
    if (
      token &&
      !config.skipRenewal &&
      isExpiringSoon(token) &&
      getStoredRefreshToken()
    ) {
      try {
        token = await renewSession(token);
      } catch (renewalError) {
        if (isSessionRejected(renewalError)) {
          endSession();
          throw renewalError;
        }
        // Network trouble: send with the token we have. If the server
        // refuses it, the 401 path below tries the renewal once more.
      }
    }

    const authToken = getAuthToken();
    config.headers = {
      ...getHeaders(config.url),
    };
    if (authToken != null) {
      config.headers["auth-token"] = authToken;
    }
    if (token != null) {
      config.headers["authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;

    // 403 = signed in but not allowed (e.g. a sub-admin on an admin-management
    // route). Everything that is not a 401 goes back to the caller as is.
    if (status !== 401 || !originalRequest || originalRequest.skipRenewal) {
      return Promise.reject(error);
    }

    // The server refused a token it issued a moment ago: the account is
    // blocked or was signed out elsewhere. A third try would not change that.
    if (originalRequest._retry) {
      endSession();
      return Promise.reject(error);
    }

    if (!getStoredRefreshToken()) {
      endSession();
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    try {
      await renewSession(sentToken(originalRequest) ?? getLocalToken());
      // The request interceptor attaches the saved token afresh on the way
      // out, so the retry carries whatever the renewal left in storage.
      return axiosInstance.request(originalRequest);
    } catch (renewalError) {
      if (isSessionRejected(renewalError)) {
        endSession();
      }
      return Promise.reject(renewalError);
    }
  },
);

export default axiosInstance;
